import React, { useMemo, useCallback } from 'react';
import { UnifiedTable } from '../../../../components/UnifiedTable';

/**
 * OzonAssortmentPanel — migrated to UnifiedTable.
 * Fixes:
 *   - Bug 1: "Артикул" was showing broken <img> (cellIndex===1 treated as image)
 *   - Bug 2: "Изображение" was showing URL text (no <img> for cellIndex===2)
 * Now column types are explicitly defined: offer_id=text, image_url=image.
 */

const ASSORTMENT_COLUMNS = [
    { key: 'rowNum', header: '№', width: 56, type: 'number', sticky: true, sortable: false, resizable: false },
    { key: 'offer_id', header: 'Артикул', width: 180, type: 'text', sticky: true },
    { key: 'image_url', header: 'Изображение', width: 80, type: 'image', imageSize: 40, sortable: false },
    {
        key: 'name',
        header: 'Название',
        width: 280,
        type: 'custom',
        render: (value, row) => {
            const sku = row.sku;
            if (!value) return <span style={{ opacity: 0.5 }}>-</span>;
            if (sku) {
                return (
                    <a
                        href={`https://ozon.ru/product/${sku}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: '#519ad6', textDecoration: 'none' }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {value}
                    </a>
                );
            }
            return value;
        },
    },
    { key: 'product_id', header: 'Product ID', width: 130, type: 'text' },
    { key: 'sku', header: 'SKU', width: 120, type: 'number' },
    { key: 'barcode', header: 'Штрихкод', width: 160, type: 'text' },
    { key: 'store_name', header: 'Название магазина', width: 180, type: 'text' },
];

// Map between column keys and header display indices (for App.js sort compatibility)
const KEY_TO_HEADER_INDEX = {
    rowNum: 0,
    offer_id: 1,
    image_url: 2,
    name: 3,
    product_id: 4,
    sku: 5,
    barcode: 6,
    store_name: 7,
};

const HEADER_INDEX_TO_KEY = Object.fromEntries(
    Object.entries(KEY_TO_HEADER_INDEX).map(([k, v]) => [v, k])
);

function OzonAssortmentPanel({
    ozonAccountId,
    ozonBusy,
    syncOzonFast,
    syncOzonData,
    assortmentPageSafe,
    assortmentTotalPages,
    assortmentPageSize,
    setAssortmentPage,
    setAssortmentPageSize,
    assortmentSearch,
    setAssortmentSearch,
    copyTable,
    downloadCsv,
    ozonAssortment,
    assortmentSliceStart,
    assortmentHeadersDisplay,
    getStickyClass,
    assortmentSort,
    setAssortmentSort,
    assortmentRowsView,
    openLightbox,
}) {
    // Convert index-based sort to key-based
    const sortKey = HEADER_INDEX_TO_KEY[assortmentSort.index] || null;
    const sortDir = assortmentSort.dir;

    // Convert key-based sort back to index-based for App.js
    const handleSortChange = useCallback(
        (key, dir) => {
            const index = KEY_TO_HEADER_INDEX[key];
            if (index != null) {
                setAssortmentSort({ index, dir });
            }
        },
        [setAssortmentSort]
    );

    // Prepare data: add rowNum to each object
    const tableData = useMemo(
        () =>
            ozonAssortment.map((item, index) => ({
                ...item,
                rowNum: assortmentSliceStart + index + 1,
            })),
        [ozonAssortment, assortmentSliceStart]
    );

    // Image click → open lightbox
    const handleImageClick = useCallback(
        (url) => {
            if (openLightbox) openLightbox([url], 0);
        },
        [openLightbox]
    );

    // Toolbar actions
    const toolbarActions = useMemo(
        () => [
            {
                label: 'Быстрое обновление',
                primary: true,
                onClick: syncOzonFast,
                disabled: !ozonAccountId || ozonBusy,
                title: 'Быстрое обновление — обновляет характеристики и новые товары в уже существующих категориях.',
            },
            {
                label: 'Полная загрузка',
                primary: true,
                onClick: syncOzonData,
                disabled: !ozonAccountId || ozonBusy,
                title: 'Полная загрузка — длительная выгрузка. Нужна при первом запуске и для полного обновления ассортимента.',
            },
        ],
        [ozonAccountId, ozonBusy, syncOzonFast, syncOzonData]
    );

    // Compute total rows from App.js pagination info
    const totalRows = assortmentTotalPages * assortmentPageSize;

    return (
        <UnifiedTable
            tableId="ozon-assortment"
            columns={ASSORTMENT_COLUMNS}
            data={tableData}
            totalRows={totalRows}
            page={assortmentPageSafe}
            pageSize={assortmentPageSize}
            onPageChange={setAssortmentPage}
            onPageSizeChange={setAssortmentPageSize}
            sortKey={sortKey}
            sortDir={sortDir}
            onSortChange={handleSortChange}
            serverSort={true}
            search={assortmentSearch}
            onSearchChange={setAssortmentSearch}
            onImageClick={handleImageClick}
            onTextOverflow={(text) => {
                if (openLightbox) {
                    // Use text lightbox mode — reuse lightbox with text
                    openLightbox([], 0);
                }
            }}
            exportFilename="ozon-assortment"
            onExportCsv={true}
            onCopyTable={true}
            toolbarActions={toolbarActions}
            loading={ozonBusy}
            getRowKey={(row, index) => `assort-${row.rowNum || index}`}
        />
    );
}

export default OzonAssortmentPanel;